import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useMemo, useState } from 'react';
import { mergedControlClasses, mergedControlTextClasses } from './input';
import { SearchableSelect } from './searchable-select';
import { cn } from '../lib/cn';
import { COUNTRIES, countryFlagEmoji, countryFor, isCountryCode, } from '../lib/countries';
import { digitsOf, E164_MAX_DIGITS, parseE164, toE164 } from '../lib/e164';
import { fieldWidthClass } from '../lib/field-width';
/**
 * PhoneInput — the phone field, once, for every app in the suite.
 *
 * A searchable country picker beside a national-number field, emitting ONE
 * string: E.164 (`+` then up to 15 digits), or `''`. Two controls, one value —
 * so a form holds `phone: string` and never a `{ country, number }` pair that
 * has to be reassembled at every call site.
 *
 * Controlled, like every other field in this package: `value` + `onChange`, no
 * internal copy of the number. It drops into react-hook-form exactly the way
 * `Input` does — `<FormControl><PhoneInput {...field} … /></FormControl>` — and
 * forwards `id`, `aria-describedby`, `aria-invalid` and its ref to the NUMBER
 * input, which is what `FormLabel`'s `htmlFor` points at.
 *
 * What it does NOT do, on purpose:
 *
 * - **No validation.** It emits a well-formed E.164 *candidate* and caps it at
 *   15 digits; whether that number is reachable is the consumer's Zod schema's
 *   question. Per-country national-number rules are not hand-rolled here.
 * - **No formatting as you type.** No `(415) 555-…` reflow: national formats are
 *   a moving target and a cursor that jumps mid-word is worse than plain digits.
 * - **No copy.** Every user-facing string is an already-translated prop, and
 *   country names can be replaced wholesale via `countryNames`. `packages/ui`
 *   never imports `@instanexus/i18n` (see the barrel's module doc).
 *
 * Renders as ONE bordered box, the same way `TimeField` does: the wrapping
 * `<div>` wears `mergedControlClasses` (`input.tsx`), the country trigger
 * (leading, flag + dial code) and the number field inside it are both
 * borderless, and the box is exactly as tall as a plain `Input`. They stay two
 * DOM elements with two accessible names (`countryLabel` / `label`), so every
 * `getByRole('combobox', { name: countryLabel })` and
 * `getByLabel(label)` that found them as two boxes still finds them; only the
 * paint is merged. It used to be two separately bordered boxes side by side,
 * which read as two fields for one value.
 *
 * Dial-code ambiguity is stated rather than hidden: +1 is 25 territories, +44 is
 * four. A parsed value displays as that dial code's declared representative
 * (`countries.ts`), an explicit pick by the user is kept as long as it still
 * dials the same code, and the emitted digits are byte-identical either way.
 */
/**
 * The popup is sized to the LIST, not to the trigger — the trigger is as narrow
 * as a flag and a dial code, and country names would be clipped to the width of
 * "+1". Hoisted out of the JSX because `contentClassName` is not on the
 * i18next `no-literal-string` attribute allowlist that `className` is on.
 */
const COUNTRY_LIST_CLASSES = 'w-72 max-w-[var(--radix-popover-content-available-width)]';
/**
 * The box width for a given NUMBER width: one token wider, to make room for the
 * country trigger that now sits inside the same box. `lg` is already the widest
 * capped token; `full` stays uncapped.
 */
const BOX_WIDTH_FOR = {
    xs: 'sm',
    sm: 'md',
    md: 'lg',
    lg: 'lg',
    full: 'full',
};
export const PhoneInput = forwardRef(function PhoneInput({ value, onChange, label, countryLabel, searchLabel, searchPlaceholder, emptyMessage, defaultCountry = 'US', countryNames, width, className, inputClassName, disabled, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, ...inputProps }, ref) {
    /**
     * The user's explicit pick, and ONLY that. The displayed country is derived
     * below; this state exists so that choosing Canada on a `+1` number is not
     * silently rewritten back to the United States on the next render — the two
     * are indistinguishable in E.164 and the value cannot remember which was meant.
     */
    const [pickedCountry, setPickedCountry] = useState(() => parseE164(value)?.country.code ?? defaultCountry);
    const parsed = useMemo(() => parseE164(value), [value]);
    const pickedDialCode = countryFor(pickedCountry).dialCode;
    /**
     * The value wins when it dials a DIFFERENT code than the pick (a reset, a
     * paste, a load from the server); the pick wins when both dial the same code,
     * which is exactly the ambiguous case the state above exists for.
     */
    const country = parsed !== null && parsed.dialCode !== pickedDialCode ? parsed.country.code : pickedCountry;
    const dialCode = countryFor(country).dialCode;
    /**
     * An unparseable non-empty value (a legacy row, a hand-edited string) still
     * shows its digits rather than an empty field that silently discards them.
     * They read as the national part, which is a guess — but a visible one the
     * user can correct, and the next keystroke re-emits a well-formed value.
     */
    const nationalNumber = parsed !== null ? parsed.nationalNumber : digitsOf(value);
    const options = useMemo(() => COUNTRIES.map((entry) => {
        const name = countryNames?.[entry.code] ?? entry.name;
        return {
            value: entry.code,
            // Name first, dial code after: the name is what a person searches and
            // what a screen reader should lead with. No flag here — a regional
            // indicator pair in the accessible name is noise a `getByRole` query
            // then has to match around.
            label: `${name} +${entry.dialCode}`,
            // The flag lives on the trigger only, where it is decoration beside a
            // dial code the user already chose.
            triggerLabel: `${countryFlagEmoji(entry.code)} +${entry.dialCode}`,
            keywords: [entry.code, entry.dialCode, `+${entry.dialCode}`, entry.name],
        };
    }), [countryNames]);
    const handleCountryChange = useCallback((next) => {
        if (!isCountryCode(next))
            return;
        setPickedCountry(next);
        const nextValue = toE164(countryFor(next).dialCode, nationalNumber);
        // Guarded: re-picking a country that dials the same code (US → Canada) must
        // not report a change the value cannot actually carry.
        if (nextValue !== value)
            onChange(nextValue);
    }, [nationalNumber, onChange, value]);
    const handleNumberChange = useCallback((event) => {
        const element = event.currentTarget;
        const digits = digitsOf(element.value).slice(0, E164_MAX_DIGITS - dialCode.length);
        // The field is controlled by `value`. When sanitising is what changed the
        // string — a typed space, a pasted dash — the emitted value can come back
        // identical, React has no state change to re-render from, and the rejected
        // character would sit in the DOM looking accepted. Write it back here so
        // the element never shows something the value does not contain.
        if (element.value !== digits)
            element.value = digits;
        onChange(toE164(dialCode, digits));
    }, [dialCode, onChange]);
    return (_jsxs("div", { className: cn(mergedControlClasses, fieldWidthClass(BOX_WIDTH_FOR[width ?? 'full']), 
        // Not a `disabled:` variant — the wrapping `<div>` is not a form
        // control and never matches `:disabled`. Both parts carry the real
        // attribute; this only dims the merged box to match (as `TimeField`).
        disabled && 'cursor-not-allowed opacity-50', className), children: [_jsx(SearchableSelect, { options: options, value: country, onValueChange: handleCountryChange, label: countryLabel, searchLabel: searchLabel, searchPlaceholder: searchPlaceholder, emptyMessage: emptyMessage, 
                // Always show the filter: 249 rows is never a list you scroll.
                searchThreshold: 0, disabled: disabled, 
                // `embedded`: no border and no `h-control` floor of its own — the box
                // wears those (see `mergedControlClasses`). `w-auto` keeps it as narrow
                // as a flag and a dial code; the number field takes the rest of the box.
                // The trailing hairline divides the leading picker from the number the
                // way a native `<select>` prefix would, without a second border box.
                triggerVariant: "embedded", className: "w-auto gap-1 rounded-e-none border-e border-border ps-3 pe-2", contentClassName: COUNTRY_LIST_CLASSES }), _jsx("input", { ...inputProps, ref: ref, type: "tel", inputMode: "tel", autoComplete: "tel-national", "aria-label": ariaLabelledBy ? undefined : (ariaLabel ?? label), "aria-labelledby": ariaLabelledBy, disabled: disabled, value: nationalNumber, onChange: handleNumberChange, className: cn(mergedControlTextClasses, inputClassName) })] }));
});
PhoneInput.displayName = 'PhoneInput';
//# sourceMappingURL=phone-input.js.map